import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../common/toast/hooks/useToast'
import { Navigation, NavigationFormProps, NavItemType } from './Navigation.d'
import { Card } from 'primereact/card'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import Common from '../../../common/Common'
import { Button, Alert, Row, Col } from 'react-bootstrap'
import { useAppSelector } from '../../../../store/hooks'
import PageHeader from '../../../layout/page/PageHeader'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import DeleteDialog from '../../../common/dialogs/DeleteDialog'
import TextField from '../../../common/forms/fields/TextField'
import { handleAxiosErrors } from '../../../common/forms/errors/Helper'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import { Tree } from 'primereact/tree'
import TreeNodeTemplate from './tree/TreeNodeTemplate'
import FormActions from '../../../common/forms/FormActions'
import NavItemDialog from './NavItemDialog'
import RenderFormikErrors from '../../../common/forms/errors/RenderFormikErrors'

const NavigationForm: FC<NavigationFormProps> = ({
    id = null,
    inPopup = true,
    config = {},
}) => {
    const isViewOrEdit = id !== null
    const theme = useAppSelector((state) => state.theme.theme)

    const navigate = useNavigate()
    const axiosPrivate = useAxiosPrivate()

    const emptyNavigation: Navigation = {
        name: '',
        code: '',
        value: [],
    }

    const NavigationFormSchema = Yup.object().shape({
        name: Yup.string().trim().required('Enter navigation name'),
        code: Yup.string()
            .trim()
            .min(3)
            .max(32)
            .required('Enter unique navigation code'),
        // value: Yup.object().required('Required'),
    })

    const [navigationDialog, setNavigationDialog] = useState(true)
    const [navigation, setNavigation] = useState<Navigation>(emptyNavigation)
    const [availablePages, setAvailablePages] = useState<[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        const routePath = isViewOrEdit
            ? '/administration/navigation/view/' + id
            : '/administration/navigation/new'

        try {
            const response = await axiosPrivate.get(routePath).finally(() => {
                setIsLoading(false)
            })
            if (response.data?.success === true) {
                setAvailablePages(response.data.data.available_pages || [])
                if (response.data.data?.navigation) {
                    setNavigation(response.data.data.navigation)
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load navigation')
            }
        }
    }, [axiosPrivate, id, isViewOrEdit])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record) => {
        setErrMsg('')
        setIsSubmitting(true)

        try {
            const routePath = isViewOrEdit
                ? '/administration/navigation/update/' + id
                : '/administration/navigation/create'
            const response = await axiosPrivate
                .post(routePath, record, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            handleAxiosErrors(err, setErrMsg, setErrorFields, config)
        }
    }

    const hideDialog = () => {
        setNavigationDialog(false)
        navigate('/administration/navigation')
    }

    const [nodes, setNodes] = useState([])

    useEffect(() => {
        setNodes(navigation.value)
    }, [navigation.value])

    const formFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={() => {
                    setNodes(navigation.value)
                    const formResetBtn = document.getElementById(
                        'navigation-form-reset-btn'
                    )
                    formResetBtn.click()
                }}
            >
                <span className="small">Reset</span>
            </Button>
            {isViewOrEdit && (
                <DeleteDialog
                    url={'/administration/navigation/delete/' + id}
                    record={navigation}
                    deletedCallback={() => hideDialog()}
                    btnLabel="Delete"
                    showIcon={false}
                    message={
                        <p className="mb-3 pb-3 border-bottom">
                            Are you sure you want to delete navigation:
                            <strong> {navigation.name}</strong>?
                        </p>
                    }
                />
            )}
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'navigation-form-submit-btn'
                    )
                    formSubmitGtn.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={{
                    ...navigation,
                }}
                validationSchema={NavigationFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
                onReset={(e, props) => {
                    const { setFieldValue } = props

                    setNodes(navigation.value || [])
                    setFieldValue('value', navigation.value || [])
                }}
            >
                {(formikProps) => {
                    return (
                        <FormikForm id="navigation-form">
                            <RenderFormikErrors formikProps={formikProps} />
                            <Row>
                                <Col sm={12}>
                                    <fieldset>
                                        <legend>General</legend>
                                        <Row>
                                            <Col xl={6}>
                                                <TextField
                                                    id="formNavigationName"
                                                    name="name"
                                                    label="Name"
                                                    placeholder="Enter navigation name"
                                                    formikProps={formikProps}
                                                    required
                                                />
                                            </Col>
                                            <Col xl={6}>
                                                <TextField
                                                    id="formNavigationCode"
                                                    name="code"
                                                    label="Code"
                                                    placeholder="Enter unique navigation code"
                                                    formikProps={formikProps}
                                                    required
                                                />
                                            </Col>
                                            <Col xl={12}>
                                                <Row>
                                                    <Col
                                                        xl={12}
                                                        className="pb-3 text-end"
                                                    >
                                                        {(() => {
                                                            return (
                                                                <NavItemDialog
                                                                    formikProps={
                                                                        formikProps
                                                                    }
                                                                    availablePages={
                                                                        availablePages
                                                                    }
                                                                    button={{
                                                                        variant:
                                                                            'success',
                                                                    }}
                                                                    setNodes={
                                                                        setNodes
                                                                    }
                                                                />
                                                            )
                                                        })()}
                                                    </Col>
                                                    <Col xl={12}>
                                                        <Tree
                                                            value={nodes}
                                                            filter
                                                            filterMode="lenient"
                                                            dragdropScope="sfy-navigation-tree"
                                                            onDragDrop={(e) => {
                                                                setNodes(
                                                                    e.value
                                                                )
                                                                formikProps.setFieldValue(
                                                                    'value',
                                                                    e.value
                                                                )
                                                            }}
                                                            emptyMessage={
                                                                <NavItemDialog
                                                                    formikProps={
                                                                        formikProps
                                                                    }
                                                                    availablePages={
                                                                        availablePages
                                                                    }
                                                                    button={{
                                                                        label: 'Navigation empty! Click here to add items',
                                                                        variant:
                                                                            'success',
                                                                    }}
                                                                    setNodes={
                                                                        setNodes
                                                                    }
                                                                />
                                                            }
                                                            nodeTemplate={(
                                                                node: NavItemType,
                                                                options
                                                            ) => {
                                                                return (
                                                                    <TreeNodeTemplate
                                                                        node={
                                                                            node
                                                                        }
                                                                        options={
                                                                            options
                                                                        }
                                                                        formikProps={
                                                                            formikProps
                                                                        }
                                                                        availablePages={
                                                                            availablePages
                                                                        }
                                                                        setNodes={
                                                                            setNodes
                                                                        }
                                                                    />
                                                                )
                                                            }}
                                                            className="w-full md:w-30rem"
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </fieldset>
                                </Col>
                            </Row>

                            <input
                                type="submit"
                                id="navigation-form-submit-btn"
                                className="d-none"
                            />
                            <input
                                type="reset"
                                id="navigation-form-reset-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={navigationDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '60vw' }}
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : navigation.name
                                : 'Create new navigation'
                        }
                        modal
                        className="p-fluid"
                        footer={formFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : ''}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    <PageHeader
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : navigation.name
                                : 'Create new navigation'
                        }
                        buttons={[
                            {
                                variant: theme === 'dark' ? 'dark' : 'light',
                                label: 'Back to Navigation',
                                icon: 'fa-solid fa-arrow-left',
                                url: '/administration/navigation',
                            },
                        ]}
                    />
                    {FormContent}
                    <FormActions formFooter={formFooter} />
                </>
            )}
        </>
    )
}

export default NavigationForm
